/**
 * Timing / Volume / Multi-Timeframe cluster — cookbook seed recipes.
 *
 * Each entry is validated against the real buildGraphFromRecipe engine.
 * Run the companion test to confirm every recipe produces zero engine errors.
 *
 * Node-type and handle references verified against:
 *   apps/frontend/src/features/backtest/schemas/node-schemas/
 */

import type { CookbookEntry } from './types'

// ── 3. Volume Confirmation ────────────────────────────────────
//
// Pattern: require that the current bar's volume exceeds the rolling session
// average by a factor of 1.5. Uses:
//   - priceData.volume (field selection on the ohlcv output) → rollingAverage
//   - multiply(rollingAverage × 1.5) as the threshold
//   - condition(current volume > threshold)
//
// Nodes:
//   priceData.volume → rollingAverage(session) → multiply(× 1.5) ─┐
//   priceData.volume                                               ├→ condition(>) → Trade Manager
const volumeConfirmation: CookbookEntry = {
  key: 'volume-confirmation',
  name: 'Volume Confirmation',
  category: 'timing-volume',
  intent: 'Enter only when current-bar volume exceeds the session average by 1.5×',
  summary: 'Require a 1.5× volume surge before entering — filters low-conviction price moves.',
  difficulty: 'beginner',
  tags: ['Breakout', 'Volume'],
  whenToUse:
    'Use with breakout or momentum strategies where a price move only carries weight when backed by elevated participation. Particularly effective during the opening hour or around key levels where false breakouts on thin volume are common.',
  whatToLookFor:
    "Look for the volume spike to coincide with a clean directional candle rather than a wick or indecision bar. If volume spikes occur frequently but price doesn't follow, consider raising the multiplier to 2× or pairing with a price-action condition.",
  howItWorks:
    "A priceData node outputs both close prices and per-bar volume. The volume stream feeds a rollingAverage node configured to the session reference period, producing a running mean. A multiply node scales that average by 1.5 to compute a dynamic threshold. A condition node compares the current bar's raw volume against the threshold and fires true only on a genuine surge. That boolean signal drives the Trade Manager's Trigger directly — no extra merge is needed because volume confirmation is the sole gate here.",
  filterSide: false,
  recipe: [
    {
      id: 'price',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // Rolling session average of volume
      id: 'avg_vol',
      type: 'rollingAverage',
      params: { reference: 'session' },
      inputs: { input_1: 'price.volume' },
    },
    {
      // Threshold = avg_volume × 1.5
      id: 'threshold',
      type: 'multiply',
      inputs: { input_1: 'avg_vol.average', input_2: 1.5 },
    },
    {
      // Fire when this bar's volume exceeds the threshold
      id: 'vol_spike',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'price.volume', input_2: 'threshold.value' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'vol_spike.true' },
  },
}

// ── 4. Multi-Timeframe Confluence ─────────────────────────────
//
// Pattern: require agreement between a fast (5m) SMA trend and a slow (1h)
// SMA trend before entering. Two separate priceData nodes carry the different
// timeframes; each feeds its own SMA, then both booleans merge via AND.
//
// Nodes:
//   price5m → sma(20, 5m) → condition(close > sma5m)  ─┐
//   price1h → sma(10, 1h) → condition(close > sma1h)  ─┴→ merge(AND) → Trade Manager
const mtfConfluence: CookbookEntry = {
  key: 'mtf-confluence',
  name: 'Multi-Timeframe Confluence',
  category: 'timing-volume',
  intent: 'Enter only when both the 5m and 1h trends agree (price above SMA on each)',
  summary: 'Require alignment between a fast 5m trend and a slow 1h trend before entering.',
  difficulty: 'intermediate',
  tags: ['Trend', 'Multi-timeframe', 'Moving average'],
  whenToUse:
    'Use when a single-timeframe signal produces too many counter-trend entries. The 1h SMA filter acts as a structural bias layer, allowing entries only when the higher timeframe is already supportive — reducing fades into larger downtrends.',
  whatToLookFor:
    'Watch for the two timeframes to diverge: if the 5m signal fires frequently while the 1h disagreement rate is high, consider widening the 1h SMA period or switching to a slower higher-timeframe reference. Confirm that the 1h SMA period (10 bars = ~10 hours) actually captures the directional bias relevant to your instrument.',
  howItWorks:
    "Two separate priceData nodes carry independent timeframe feeds: one at 5m and one at 1h. Each feeds its own simplemovingaverage node (period 20 on the 5m, period 10 on the 1h). Two condition nodes independently test whether close > SMA on each timeframe, each emitting a boolean. A merge(AND) node requires both booleans to be true simultaneously, forming the confluence signal. Only when both the fast and slow trends agree does the result flow to the Trade Manager's Trigger — aligning with the core multi-timeframe principle that the higher timeframe sets directional bias and the lower timeframe provides the entry timing.",
  filterSide: false,
  recipe: [
    {
      // Primary (fast) timeframe
      id: 'price5m',
      type: 'priceData',
      params: { timeframe: '5m' },
    },
    {
      // Higher timeframe
      id: 'price1h',
      type: 'priceData',
      params: { timeframe: '1h' },
    },
    {
      id: 'sma5m',
      type: 'simplemovingaverage',
      params: { period: 20 },
      inputs: { input_1: 'price5m.close' },
    },
    {
      id: 'sma1h',
      type: 'simplemovingaverage',
      params: { period: 10 },
      inputs: { input_1: 'price1h.close' },
    },
    {
      // 5-minute trend: close above its own SMA
      id: 'trend5m',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'price5m.close', input_2: 'sma5m.sma' },
    },
    {
      // 1-hour trend: 1h close above 1h SMA
      id: 'trend1h',
      type: 'condition',
      params: { operator: '>' },
      inputs: { input_1: 'price1h.close', input_2: 'sma1h.sma' },
    },
    {
      // Both timeframes must agree
      id: 'confluence',
      type: 'merge',
      params: { operator: 'AND' },
      inputs: { input_1: 'trend5m.true', input_2: 'trend1h.true' },
    },
  ],
  tradeManager: {
    params: { orderDirection: 'Long' },
    feeds: { socket: 'trigger', from: 'confluence.result' },
  },
}

// ── Export ───────────────────────────────────────────────────

export const timingVolumeSeedRecipes: CookbookEntry[] = [volumeConfirmation, mtfConfluence]
